import type { RenderStatusType } from '@/shared/constants/status';
import type { RenderSettings } from '@/types/render';

/**
 * Unified type for concept history items (legacy - for project page)
 */
export interface HistoryItem {
  type: 'concept-history';
  // For job-history items this is the render/album ID, not the concept ID — use
  // `conceptId` for navigation. Mirrors Apple `HistoryItem.id` / Android `HistoryItem.id`.
  id: string;
  conceptId?: string;
  conceptAngleId?: string;
  attributes: {
    name: string;
    project_sequence_number: number;
    created_at: string;
    updated_at: string;
    is_favorite: boolean;
    image_url: string;
    image_type: 'render' | 'angle';
    render_count: number;
    has_multiplerenders: boolean;
    render_id?: string | null;
    is_upscaled?: boolean;
    is_main_angle?: boolean;
    /** Render mode — drives Exploration/Refinement badge in carousels */
    mode?: 'explore' | 'refine';
    activerender?: {
      id: string;
      status: RenderStatusType;
      started_at?: string;
      duration_predicted?: number;
    } | null;
    // Album-specific fields (optional)
    is_album?: boolean;
    album_id?: string;
    album_template?: string;
    // Whether blueprints were bundled (server-driven). Replaces the old
    // full/short template axis for any blueprint-inclusion display.
    album_include_blueprints?: boolean;
    album_format?: string;
    album_language?: string;
    album_filename?: string;
    album_file_size?: string;
    album_page_count?: number;
    album_public_url?: string;
    album_status?: JobStatus;
    album_started_at?: string;
    album_duration_predicted?: number;
    album_cover_image_url?: string;
    album_title?: string;
    // Estimate-specific fields (optional)
    is_estimate?: boolean;
    estimate_id?: string;
    /** Display currency; money amounts are display-converted server-side. */
    estimate_currency?: string | null;
    estimate_materials_total?: number;
    estimate_labor_total?: number;
    estimate_grand_total?: number;
    estimate_region_label?: string | null;
    estimate_status?: JobStatus;
    estimate_started_at?: string;
    estimate_duration_predicted?: number;
    estimate_failure_reason?: string;
    // Angle URL for failed render display
    angle_url?: string;
  };
  relationships: {
    project: {
      data: {
        type: 'projects';
        id: string;
      };
      meta: {
        name: string;
      };
    };
  };
}

/**
 * Job status values
 */
export type JobStatus = 'pending' | 'processing' | 'completed' | 'failed';

/**
 * Base interface for job history items
 */
interface JobHistoryItemBase {
  id: string;
  attributes: {
    status: JobStatus;
    created_at: string;
    started_at?: string;
    completed_at?: string;
    duration_predicted?: number;
  };
  relationships: {
    concept: {
      data: {
        type: 'concepts';
        id: string;
      } | null;
      meta: {
        name?: string;
        project_sequence_number: number;
      };
    };
    project: {
      data: {
        type: 'projects';
        id: string;
      } | null;
      meta: {
        name: string;
      };
    };
  };
}

/**
 * Type for render history items (for dashboard and history screen)
 */
export interface RenderHistoryItem extends JobHistoryItemBase {
  type: 'job-render';
  attributes: JobHistoryItemBase['attributes'] & {
    image_url?: string; // Optional - only present for completed renders
    angle_url?: string; // Original angle image URL (for failed render display)
    is_upscaled: boolean;
    mode?: 'explore' | 'refine';
    settings?: RenderSettings;
  };
  relationships: JobHistoryItemBase['relationships'] & {
    concept_angle?: {
      data: {
        type: 'concept_angle';
        id: string;
      } | null;
      meta: {
        is_main: boolean;
        angle_id?: string;
      };
    };
  };
}

/**
 * Type for album history items
 */
export interface AlbumHistoryItem extends JobHistoryItemBase {
  type: 'job-album';
  attributes: JobHistoryItemBase['attributes'] & {
    template: string;
    // Whether blueprints were bundled (server-driven, replaces full/short).
    include_blueprints?: boolean;
    format: string;
    language: string;
    filename?: string;
    file_size?: string;
    page_count?: number;
    public_url?: string;
    image_url?: string;
    album_title?: string;
  };
}

/**
 * Type for cost-estimate history items
 */
export interface EstimateHistoryItem extends JobHistoryItemBase {
  type: 'job-estimate';
  attributes: JobHistoryItemBase['attributes'] & {
    /** Raw estimate status (`generating`/`ready`) — `status` carries the unified job vocabulary. */
    estimate_status?: string;
    /** Display currency; money amounts are already display-converted server-side. */
    currency?: string | null;
    materials_total?: number;
    labor_total?: number;
    grand_total?: number;
    region_label?: string | null;
    country_code?: string | null;
    is_edited?: boolean;
    failure_reason?: string;
    /** Concept main render — the card thumbnail. */
    image_url?: string;
  };
}

/**
 * Discriminated union for job history items
 */
export type JobHistoryItem =
  | RenderHistoryItem
  | AlbumHistoryItem
  | EstimateHistoryItem;

/**
 * Type guards for job history items
 */
export function isRenderHistoryItem(
  item: JobHistoryItem
): item is RenderHistoryItem {
  return item.type === 'job-render';
}

export function isAlbumHistoryItem(
  item: JobHistoryItem
): item is AlbumHistoryItem {
  return item.type === 'job-album';
}

export function isEstimateHistoryItem(
  item: JobHistoryItem
): item is EstimateHistoryItem {
  return item.type === 'job-estimate';
}

/**
 * Check if job is active (pending or processing)
 */
export function isActiveJob(item: JobHistoryItem): boolean {
  return (
    item.attributes.status === 'pending' ||
    item.attributes.status === 'processing'
  );
}

/**
 * Check if job is completed
 */
export function isCompletedJob(item: JobHistoryItem): boolean {
  return item.attributes.status === 'completed';
}

export interface HistoryFilters {
  is_favorite?: boolean;
  project_id?: string;
  search?: string;
  status?: JobStatus | JobStatus[]; // Filter by status (single or multiple)
  subtype?: 'render' | 'album' | 'estimate'; // Filter by job subtype
}

export type HistorySortBy = 'date-desc' | 'date-asc' | 'name-asc' | 'name-desc';

export interface HistoryParams {
  filter?: HistoryFilters;
  sort?: string;
  page?: {
    size?: number;
    number?: number;
  };
}

export interface HistoryResponse {
  data: HistoryItem[];
  meta?: {
    total?: number;
    page?: number;
    per_page?: number;
  };
  links?: {
    first?: string;
    last?: string;
    next?: string;
    prev?: string;
  };
}

export interface RenderHistoryResponse {
  data: RenderHistoryItem[];
  meta?: {
    total?: number;
    page?: number;
    per_page?: number;
    last_page?: number;
  };
  links?: {
    first?: string;
    last?: string;
    next?: string;
    prev?: string;
  };
}

export interface JobHistoryResponse {
  data: JobHistoryItem[];
  meta?: {
    total?: number;
    current_page?: number;
    per_page?: number;
    last_page?: number;
  };
  links?: {
    first?: string;
    last?: string;
    next?: string;
    prev?: string;
  };
}

/**
 * Convert RenderHistoryItem to HistoryItem format for UI compatibility
 * This allows reusing existing ConceptCard component
 */
export function renderHistoryToHistoryItem(
  render: RenderHistoryItem
): HistoryItem {
  const isActive = isActiveJob(render);

  return {
    type: 'concept-history',
    // Use render ID as unique key to avoid duplicate keys when same concept has multiple renders
    id: render.id,
    conceptId: render.relationships.concept.data?.id,
    conceptAngleId: render.relationships.concept_angle?.data?.id,
    attributes: {
      name: render.relationships.concept.meta.name || '',
      project_sequence_number:
        render.relationships.concept.meta.project_sequence_number,
      created_at: render.attributes.created_at,
      updated_at:
        render.attributes.completed_at || render.attributes.created_at,
      is_favorite: false, // Render doesn't have favorite, it's on concept level
      image_url: render.attributes.image_url || '',
      image_type: 'render',
      render_count: 1,
      has_multiplerenders: false,
      render_id: render.id,
      is_upscaled: render.attributes.is_upscaled,
      is_main_angle: render.relationships.concept_angle?.meta.is_main ?? true,
      mode: render.attributes.mode,
      // Show active render info for pending/processing jobs
      activerender: isActive
        ? {
            id: render.id,
            status: render.attributes.status,
            started_at: render.attributes.started_at,
            duration_predicted: render.attributes.duration_predicted,
          }
        : null,
      // Angle URL for failed render display
      angle_url: render.attributes.angle_url,
    },
    relationships: {
      project: {
        data: {
          type: 'projects',
          id: render.relationships.project.data?.id ?? '',
        },
        meta: {
          name: render.relationships.project.meta.name,
        },
      },
    },
  };
}

/**
 * Convert JobHistoryItem (render or album) to HistoryItem format for UI compatibility
 * Albums are displayed with a PDF icon instead of image
 */
export function jobHistoryToHistoryItem(item: JobHistoryItem): HistoryItem {
  if (isRenderHistoryItem(item)) {
    return renderHistoryToHistoryItem(item);
  }

  if (isEstimateHistoryItem(item)) {
    return {
      type: 'concept-history',
      id: item.id,
      conceptId: item.relationships.concept.data?.id,
      attributes: {
        name: item.relationships.concept.meta.name || '',
        project_sequence_number:
          item.relationships.concept.meta.project_sequence_number,
        created_at: item.attributes.created_at,
        updated_at: item.attributes.completed_at || item.attributes.created_at,
        is_favorite: false,
        image_url: item.attributes.image_url || '',
        image_type: 'render',
        render_count: 0,
        has_multiplerenders: false,
        render_id: undefined,
        is_upscaled: false,
        is_main_angle: true,
        activerender: null,
        // Custom fields for estimate display
        is_estimate: true,
        estimate_id: item.id,
        estimate_currency: item.attributes.currency ?? null,
        estimate_materials_total: item.attributes.materials_total,
        estimate_labor_total: item.attributes.labor_total,
        estimate_grand_total: item.attributes.grand_total,
        estimate_region_label: item.attributes.region_label ?? null,
        estimate_status: item.attributes.status,
        estimate_started_at: item.attributes.started_at,
        estimate_duration_predicted: item.attributes.duration_predicted,
        estimate_failure_reason: item.attributes.failure_reason,
      },
      relationships: {
        project: {
          data: {
            type: 'projects',
            id: item.relationships.project.data?.id ?? '',
          },
          meta: {
            name: item.relationships.project.meta.name,
          },
        },
      },
    };
  }

  // Album item - use album ID as unique key to avoid duplicate keys
  return {
    type: 'concept-history',
    id: item.id,
    conceptId: item.relationships.concept.data?.id,
    attributes: {
      name: item.relationships.concept.meta.name || '',
      project_sequence_number:
        item.relationships.concept.meta.project_sequence_number,
      created_at: item.attributes.created_at,
      updated_at: item.attributes.completed_at || item.attributes.created_at,
      is_favorite: false,
      image_url: item.attributes.image_url || '',
      image_type: 'render', // Will be overridden by album-specific UI
      render_count: 0,
      has_multiplerenders: false,
      render_id: undefined,
      is_upscaled: false,
      is_main_angle: true,
      activerender: null,
      // Custom fields for album display
      is_album: true,
      album_id: item.id,
      album_template: item.attributes.template,
      album_include_blueprints: item.attributes.include_blueprints ?? false,
      album_format: item.attributes.format,
      album_language: item.attributes.language,
      album_filename: item.attributes.filename,
      album_file_size: item.attributes.file_size,
      album_page_count: item.attributes.page_count,
      album_public_url: item.attributes.public_url,
      album_status: item.attributes.status,
      album_started_at: item.attributes.started_at,
      album_duration_predicted: item.attributes.duration_predicted,
      album_cover_image_url: item.attributes.image_url,
      album_title: item.attributes.album_title,
    },
    relationships: {
      project: {
        data: {
          type: 'projects',
          id: item.relationships.project.data?.id ?? '',
        },
        meta: {
          name: item.relationships.project.meta.name,
        },
      },
    },
  };
}
